import * as React from "react";
import { BaseComponent } from "../../../../../../../react-core/Html/BaseComponent";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { ClientAccount } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/ClientAccount";
import { PersonalDetail } from "../../../../../../../Wrap/Models/Models/Customer/PersonalDetail";
import { ClientRecordTab } from "../../../ClientRecord/Resource/Contents";
import { ClientRecordStore } from "../../../ClientRecord/Store/Variable";
import { PortfolioDashboardAction } from "../../Action/PortfolioDashboardAction";
import { PortfolioDashboardMultiSelectionStore, PortfolioDashboardStore } from "../../Store/Variable";
import { portfolioDashboardTabEnum } from "../../Resource/Content";
import { ContextStore } from "../../../../../../../react-core/Flux/Variables";
import { ContextType } from "../../../../../../../Wrap/Constants/Enums/ContextType";
import { headAccountGroupByInvestorType, selectedHeadAccountsOnly } from "./PortfolioDashboardHelper";
import { ServiceProposition } from "../../../../../../../Wrap/Constants/Enums/ServiceProposition";

interface IMultiSelectionProps {
    onClose: VoidFunction;
    name: string;
    isConsolidated: boolean;
    portfolioDashboardTabEnum: portfolioDashboardTabEnum;
}

interface IMultiSelectionState {
    selectedAccounts: string[];
    clientInfo: PersonalDetail;
    allAccounts: ClientAccount[];
}

export class MultiSelection extends BaseComponent<IMultiSelectionProps, IMultiSelectionState> {
    constructor(props: any) {
        super(props);
        this.state = {
            selectedAccounts: PortfolioDashboardMultiSelectionStore.getData(),
            clientInfo: ClientRecordStore.getData()?.[ClientRecordTab.PersonalDetail],
            allAccounts: PortfolioDashboardStore.getData(),
        };
        this.subscribeToStore(PortfolioDashboardMultiSelectionStore, () => this.onStoreChange());
        this.subscribeToStore(PortfolioDashboardStore, () => this.onDashboardStoreChange());
    }

    private setSelection() {
        this.props.onClose();
        PortfolioDashboardAction.setMultiSelection(
            ContextStore.getUrlParamsHierarchyId(ContextType.Customer),
            this.state.selectedAccounts,
            false,
            () => {}
        );
    }

    private onStoreChange() {
        this.setState({ selectedAccounts: PortfolioDashboardMultiSelectionStore.getData() });
    }

    private onDashboardStoreChange() {
        this.setState({ allAccounts: PortfolioDashboardStore.getData() });
    }

    private renderSingleAccount(account: ClientAccount, selectedAccounts: string[], key: string): JSX.Element {
        const headAccountSeleted = (account.SubAccounts || []).every((ele) =>
            selectedAccounts.includes(ele.HierarchyId)
        );
        return (
            <>
                <hr className="mb-17" />
                <div className="item is-all">
                    <CheckboxEditor
                        label={`${key} - ${account.HeadClAccountId}`}
                        cssWrapper="square-checkbox  mb-0 "
                        value={headAccountSeleted}
                        labelGridClasses={`account-name ${
                            account.AccountServicePropositionId == ServiceProposition.Fundzone ? "isFz" : ""
                        }`}
                        onChange={(v) => {
                            let _selection = this.state.selectedAccounts.concat();
                            if (v) {
                                // push all subaccounts into selection
                                _selection = _selection.concat(
                                    (account.SubAccounts || []).map((subaccount) => subaccount.HierarchyId)
                                );
                            } else {
                                // remove all subaccounts from selection
                                _selection = _selection.filter(
                                    (selection) =>
                                        !(account.SubAccounts || []).some(
                                            (subaccount) => subaccount.HierarchyId === selection
                                        )
                                );
                            }
                            this.setState({ selectedAccounts: _selection });
                        }}
                    />

                    {(account.SubAccounts || []).map((subAccount) => {
                        return (
                            <div className="item">
                                <CheckboxEditor
                                    label={`${subAccount.ProductDisplayName} - ${subAccount.ClAccountId}`}
                                    cssWrapper="square-checkbox mb-0"
                                    value={this.state.selectedAccounts.includes(subAccount.HierarchyId)}
                                    onChange={(v) => {
                                        let _selection = this.state.selectedAccounts.concat();
                                        if (v) {
                                            _selection = _selection.concat(subAccount.HierarchyId);
                                        } else {
                                            _selection = _selection.filter(
                                                (selection) => selection !== subAccount.HierarchyId
                                            );
                                        }
                                        this.setState({ selectedAccounts: _selection });
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            </>
        );
    }

    public render(): JSX.Element {
        const { selectedAccounts, allAccounts } = this.state;
        if (!selectedAccounts) {return <PartLoading isLoading={true} />;}
        if (!allAccounts) {return <PartLoading isLoading={true} />;}
        const { isConsolidated, name } = this.props;
        const groupHeadaccount = headAccountGroupByInvestorType(allAccounts);
        return (
            <>
                <p className="item is-all">{`Select the data to view from the ${
                    isConsolidated ? "Consolidated" : "clients overall"
                } accounts:`}</p>
                <div className="item is-all">
                    <CheckboxEditor
                        label={`${isConsolidated ? `Consolidated View ${name}` : "Total account view"}`}
                        cssWrapper="square-checkbox mb-0"
                        value={selectedHeadAccountsOnly(selectedAccounts, allAccounts)?.length == allAccounts?.length}
                        onChange={(v) => {
                            let _selection = [];
                            if (v) {
                                allAccounts.forEach((clientaccount) => {
                                    _selection = _selection.concat(
                                        clientaccount.SubAccounts.map((account) => account.HierarchyId) || []
                                    );
                                });
                            }
                            this.setState({ selectedAccounts: _selection });
                        }}
                    />
                </div>

                {groupHeadaccount.Individual.map((account) => {
                    return this.renderSingleAccount(account, selectedAccounts, "Individual");
                })}

                {groupHeadaccount.Joint.map((account) => {
                    return this.renderSingleAccount(account, selectedAccounts, "Joint");
                })}

                {groupHeadaccount.Trust.map((account) => {
                    return this.renderSingleAccount(account, selectedAccounts, "Trust");
                })}
                <button className="btn secondary ml-16 mb-10" onClick={() => this.props.onClose()}>
                    Cancel
                </button>
                <button
                    className="btn primary ml-16 mb-10"
                    onClick={() => {
                        this.setSelection();
                    }}
                    disabled={
                        selectedAccounts.length == 0 ||
                        this.props.portfolioDashboardTabEnum == portfolioDashboardTabEnum.Transactions ||
                        this.props.portfolioDashboardTabEnum == portfolioDashboardTabEnum.Documents
                    }
                >
                    Apply
                </button>
            </>
        );
    }
}
